import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types";
import { workTypeLabel } from "@/types";

interface HeroCarouselProps {
  items: Work[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStartX = useRef(0);

  const go = useCallback(
    (dir: number) => {
      setActive((prev) => (prev + dir + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  const restart = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
  };

  if (items.length === 0) return null;

  return (
    <section
      className="relative w-full overflow-hidden"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 40) {
          go(delta < 0 ? 1 : -1);
          restart();
        }
      }}
    >
      <div className="relative h-[440px] w-full md:h-[560px] lg:h-[600px]">
        {items.map((work, idx) => (
          <Link
            key={work.id}
            to={`/detail/${work.id}`}
            className={`absolute inset-0 transition-opacity duration-700 ${
              idx === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={idx !== active}
          >
            <img
              src={work.poster}
              alt={`${work.title}焦点海报`}
              title={`${work.title} - 糖心传媒焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/50 to-foreground-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30" />
          </Link>
        ))}

        {/* Text content */}
        <div className="pointer-events-none absolute inset-0 flex items-center">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="max-w-2xl">
              {items.map((work, idx) => (
                <div
                  key={work.id}
                  className={idx === active ? "pointer-events-auto animate-fade-in-up" : "hidden"}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                      {workTypeLabel[work.type]}
                    </span>
                    <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                      焦点推荐
                    </span>
                    <span className="flex items-center gap-1 text-sm font-semibold text-accent-400">
                      <i className="ri-star-fill" />
                      {work.rating.toFixed(1)}
                    </span>
                  </div>
                  <h2 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-5xl">
                    {work.title}
                  </h2>
                  <p className="mt-2 text-sm text-background-100/80 md:text-base">
                    {work.enTitle} · {work.year} · {work.genre.join(" / ")}
                  </p>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                    {work.summary}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${work.id}`}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      查看详情
                    </Link>
                    <Link
                      to={`/category/${work.type}`}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/10"
                    >
                      更多{workTypeLabel[work.type]}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Controls */}
        {items.length > 1 && (
          <>
            <button
              type="button"
              aria-label="上一张"
              onClick={() => {
                go(-1);
                restart();
              }}
              className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => {
                go(1);
                restart();
              }}
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
            <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
              {items.map((work, idx) => (
                <button
                  key={work.id}
                  type="button"
                  aria-label={`切换到第 ${idx + 1} 张`}
                  onClick={() => {
                    setActive(idx);
                    restart();
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === active ? "w-7 bg-primary-500" : "w-2.5 bg-background-50/50"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}